import { LogLevel } from '../diagnostics/log-level';
import { LogRecord } from '../diagnostics/log-record';
import { Logging } from '../diagnostics/logging';
import { Strings } from '../generated/strings';

/**
 * The interface of resource query.
 */
export interface ResourceCacheFind<T> {
    /**
     * Find a resource by id.
     *
     * @param id the identification.
     * @return T the found object if any.
     */
    find: (id: string) => T;
}

/**
 * Resource service to find a resource data by type and id.
 */
export class ResourceCache {
    public static svgType = 'svg';
    public static svgInlineType = 'svgInline';
    public static strings = 'strings';

    private static collection: { [type: string]: ResourceCacheFind<any> } = {};

    /**
     * Register resource service to a specific type.
     *
     * @param type the type name of resource.
     * @param resource the resource data with find() interface.
     */
    public register(type: string, resource: ResourceCacheFind<any>): void {
        const existingResource = ResourceCache.collection[type];
        if (existingResource) {
            const mergedResource = MsftSme.deepAssign(existingResource, resource);
            ResourceCache.collection[type] = mergedResource;
        } else {
            ResourceCache.collection[type] = resource;
        }
    }

    /**
     * Find a resource for the type and id.
     *
     * @param type the type name of resource.
     * @param id the identification.
     * @return T the found object id any.
     */
    public find<T>(type: string, id: string): T {
        const resource = ResourceCache.collection[type];
        if (!resource) {
            Logging.log(<LogRecord>{
                source: 'ResourceCache',
                level: LogLevel.Warning,
                message: MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.ResourceCacheUnableFind.message.format(type)
            });
            return null;
        }

        return <T>resource.find(id);
    }

    /**
     * @deprecated - Use MsftSme.getStrings<T>() instead.
     *
     * Gets the localized strings initialized by localization manager. The LocalizationManager should have
     * been used to get the localized strings. This can also be achieved by calling SmeEnvironment.initEnvironment().
     * @returns an object containing all the localized strings, or null if noe localized strings have been fetched yet
     */
    public getStrings<T>(): T {
        const global: MsftSme.SMEWindow = <any>window;
        return global.MsftSme.Resources && <T>global.MsftSme.Resources.strings;
    }
}
